export const SIMPLE_BOT_SETUP_KEY = 'pinngro:simple-bot-setup:v1';
export const SIMPLE_BOT_SETUP_SCHEMA_VERSION = 4;

const BOT_ORDINAL_WORDS = Object.freeze([
  'first', 'second', 'third', 'fourth', 'fifth',
  'sixth', 'seventh', 'eighth', 'ninth', 'tenth',
  'eleventh', 'twelfth', 'thirteenth', 'fourteenth', 'fifteenth',
  'sixteenth', 'seventeenth', 'eighteenth', 'nineteenth', 'twentieth',
]);

const numericOrdinal = (position) => {
  const remainder100 = position % 100;
  if (remainder100 >= 11 && remainder100 <= 13) return `${position}th`;
  if (position % 10 === 1) return `${position}st`;
  if (position % 10 === 2) return `${position}nd`;
  if (position % 10 === 3) return `${position}rd`;
  return `${position}th`;
};

export const defaultBotNameForPosition = (position) => {
  const safePosition = Math.max(Number.parseInt(position, 10) || 1, 1);
  const ordinal = BOT_ORDINAL_WORDS[safePosition - 1] || numericOrdinal(safePosition);
  return `My ${ordinal} Pinngro bot`;
};

export const nextDefaultBotName = (ownedBots = []) => {
  const bots = Array.isArray(ownedBots) ? ownedBots : [];
  const existingNames = new Set(bots.map((bot) => (
    String(bot?.name || '').trim().toLocaleLowerCase()
  )).filter(Boolean));
  let position = bots.length + 1;
  let candidate = defaultBotNameForPosition(position);
  while (existingNames.has(candidate.toLocaleLowerCase())) {
    position += 1;
    candidate = defaultBotNameForPosition(position);
  }
  return candidate;
};

export const EMPTY_SIMPLE_BOT_SETUP = Object.freeze({
  name: 'My first Pinngro bot',
  nameSource: 'suggested',
  discoveryMode: 'STOCK_FOCUS',
  preset: 'Blue-Chip Stability',
  tradingStrategy: 'trend-pullback',
  symbols: [],
  screeningLineage: null,
  selectionMethod: null,
  pickSession: null,
  maximumAllocation: '10',
  newsSentimentEnabled: false,
  savedAt: null,
});

const normalizeSymbols = (symbols) => Array.from(new Set(
  (Array.isArray(symbols) ? symbols : [])
    .map((symbol) => String(symbol || '').trim().toUpperCase())
    .filter((symbol) => /^[A-Z][A-Z0-9.-]{0,14}$/.test(symbol)),
));

const normalizeSelectionMethod = (value) => (
  value === 'RANDOM_STOCK_FOCUS' ? 'RANDOM_STOCK_FOCUS' : null
);

const normalizeScreeningLineage = (lineage) => {
  if (!lineage || typeof lineage !== 'object') return null;
  const generationId = String(lineage.generationId || '');
  const generationHash = String(lineage.generationHash || '').toLowerCase();
  const profileId = String(lineage.profileId || '').slice(0, 100);
  const profileSchemaVersion = String(lineage.profileSchemaVersion || '').slice(0, 64);
  const profileBundleHash = String(lineage.profileBundleHash || '').toLowerCase();
  if (
    !/^[0-9a-f-]{36}$/.test(generationId)
    || !/^[0-9a-f]{64}$/.test(generationHash)
    || !profileId
    || !profileSchemaVersion
    || !/^[0-9a-f]{64}$/.test(profileBundleHash)
  ) return null;
  return {
    generationId,
    generationHash,
    profileId,
    profileSchemaVersion,
    profileBundleHash,
  };
};

const normalizePickSession = (session, symbols) => {
  if (!session || typeof session !== 'object') return null;
  const requestedCount = Number(session.requestedCount);
  if (![5, 10, 20].includes(requestedCount)) return null;
  const currentSymbols = normalizeSymbols(symbols);
  const keptSymbols = normalizeSymbols(session.keptSymbols).filter((symbol) => currentSymbols.includes(symbol));
  const companiesBySymbol = new Map(
    (Array.isArray(session.selectedCompanies) ? session.selectedCompanies : [])
      .map((item) => {
        const symbol = normalizeSymbols([item?.symbol])[0];
        return symbol ? [symbol, {
          symbol,
          company_name: typeof item?.company_name === 'string' ? item.company_name.slice(0, 200) : null,
        }] : null;
      })
      .filter(Boolean),
  );
  return {
    requestedCount,
    stockFocusMatchCount: Number.isInteger(session.stockFocusMatchCount)
      && session.stockFocusMatchCount >= 0
      ? session.stockFocusMatchCount
      : Number.isInteger(session.eligibleCount) && session.eligibleCount >= 0
        ? session.eligibleCount
        : null,
    eligibleCount: Number.isInteger(session.eligibleCount) && session.eligibleCount >= 0
      ? session.eligibleCount
      : null,
    stockFocus: typeof session.stockFocus === 'string' ? session.stockFocus.slice(0, 100) : null,
    keptSymbols,
    selectedCompanies: currentSymbols.map((symbol) => companiesBySymbol.get(symbol) || { symbol, company_name: null }),
  };
};

export const simpleBotSetupOwnerFromToken = (token) => {
  try {
    const payloadSegment = String(token || '').split('.')[1];
    if (!payloadSegment) return null;
    const normalized = payloadSegment.replace(/-/g, '+').replace(/_/g, '/');
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
    const payload = JSON.parse(globalThis.atob(padded));
    const subject = String(payload?.sub || '');
    return /^[A-Za-z0-9._:-]{1,128}$/.test(subject) ? subject : null;
  } catch {
    return null;
  }
};

export const readSimpleBotSetup = (ownerScope, storage = window.sessionStorage) => {
  try {
    const value = JSON.parse(storage.getItem(SIMPLE_BOT_SETUP_KEY) || 'null');
    if (!value || typeof value !== 'object') return { ...EMPTY_SIMPLE_BOT_SETUP };
    if (
      !ownerScope
      || ![1, 2, 3, SIMPLE_BOT_SETUP_SCHEMA_VERSION].includes(value.schemaVersion)
      || value.ownerScope !== ownerScope
    ) {
      storage.removeItem(SIMPLE_BOT_SETUP_KEY);
      return { ...EMPTY_SIMPLE_BOT_SETUP };
    }
    return {
      name: typeof value.name === 'string' ? value.name.slice(0, 100) : '',
      nameSource: value.nameSource === 'suggested' || value.nameSource === 'user'
        ? value.nameSource
        : value.name === EMPTY_SIMPLE_BOT_SETUP.name ? 'suggested' : 'user',
      discoveryMode: value.discoveryMode === 'ALL_STOCKS' ? 'ALL_STOCKS' : 'STOCK_FOCUS',
      preset: typeof value.preset === 'string' ? value.preset.slice(0, 100) : '',
      tradingStrategy: typeof value.tradingStrategy === 'string'
        ? value.tradingStrategy.slice(0, 100)
        : '',
      symbols: normalizeSymbols(value.symbols),
      screeningLineage: normalizeScreeningLineage(value.screeningLineage),
      selectionMethod: normalizeSelectionMethod(value.selectionMethod),
      pickSession: normalizePickSession(value.pickSession, value.symbols),
      maximumAllocation: typeof value.maximumAllocation === 'string'
        ? value.maximumAllocation.slice(0, 12)
        : '10',
      newsSentimentEnabled: value.newsSentimentEnabled === true,
      savedAt: typeof value.savedAt === 'string' ? value.savedAt : null,
    };
  } catch {
    try {
      storage.removeItem(SIMPLE_BOT_SETUP_KEY);
    } catch {
      // Corrupt browser storage is ignored when it cannot be cleared.
    }
    return { ...EMPTY_SIMPLE_BOT_SETUP };
  }
};

export const writeSimpleBotSetup = (draft, ownerScope, storage = window.sessionStorage) => {
  const symbols = normalizeSymbols(draft?.symbols);
  const selectionMethod = normalizeSelectionMethod(draft?.selectionMethod);
  const safeDraft = {
    name: String(draft?.name || '').slice(0, 100),
    nameSource: draft?.nameSource === 'suggested' ? 'suggested' : 'user',
    discoveryMode: draft?.discoveryMode === 'ALL_STOCKS' ? 'ALL_STOCKS' : 'STOCK_FOCUS',
    preset: String(draft?.preset || '').slice(0, 100),
    tradingStrategy: String(draft?.tradingStrategy || '').slice(0, 100),
    symbols,
    screeningLineage: normalizeScreeningLineage(draft?.screeningLineage),
    selectionMethod,
    pickSession: selectionMethod === 'RANDOM_STOCK_FOCUS'
      ? normalizePickSession(draft?.pickSession, symbols)
      : null,
    maximumAllocation: String(draft?.maximumAllocation ?? '10').slice(0, 12),
    newsSentimentEnabled: draft?.newsSentimentEnabled === true,
    savedAt: new Date().toISOString(),
  };
  if (!ownerScope) return safeDraft;
  try {
    storage.setItem(SIMPLE_BOT_SETUP_KEY, JSON.stringify({
      schemaVersion: SIMPLE_BOT_SETUP_SCHEMA_VERSION,
      ownerScope,
      ...safeDraft,
    }));
  } catch {
    // Setup remains usable in memory when browser storage is unavailable.
  }
  return safeDraft;
};

export const clearSimpleBotSetup = (storage = window.sessionStorage) => {
  try {
    storage.removeItem(SIMPLE_BOT_SETUP_KEY);
  } catch {
    // Logout and successful creation must not fail because storage is unavailable.
  }
};

export const updateSimpleBotSymbols = (symbols, ownerScope, storage = window.sessionStorage) => {
  const current = readSimpleBotSetup(ownerScope, storage);
  return writeSimpleBotSetup({
    ...current,
    symbols: normalizeSymbols(symbols),
    screeningLineage: null,
    selectionMethod: null,
    pickSession: null,
  }, ownerScope, storage);
};

export const updateSimpleBotSelection = (
  symbols,
  screeningLineage,
  ownerScope,
  storage = window.sessionStorage,
  selectionMethod = null,
  pickSession = undefined,
) => {
  const current = readSimpleBotSetup(ownerScope, storage);
  return writeSimpleBotSetup({
    ...current,
    symbols: normalizeSymbols(symbols),
    screeningLineage: normalizeScreeningLineage(screeningLineage),
    selectionMethod: normalizeSelectionMethod(selectionMethod),
    pickSession: pickSession === undefined ? current.pickSession : pickSession,
  }, ownerScope, storage);
};

export const parseSymbolInput = (value) => normalizeSymbols(String(value || '').split(','));
